import * as React from "react";
import {
  Link,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import Grid from '@mui/material/Grid2';
import dayjs from "dayjs";
import _ from "lodash";
import { useSnackbar } from "notistack";
import { useLocation, useNavigate } from "react-router-dom";

// import Zustand store
import { useAppStore } from "../../../stores";
import { getAuditSummary } from "../../../actions/AuditAndRevisions/AuditAndRevisionsActions";
import AuditAndHistoryRevisionSingleRecordContentDisplay from "./AuditAndHistoryRevisionSingleRecordContentDisplay";
import { setComment } from "../../../actions/AuditAndRevisions/AuditAndRevisionsActions";
import GenericSpinner from "../../../components/GenericSpinner/GenericSpinner";

interface IRevisionContent {
  current?: {
    detail: string;
    createTimestamp: string;
    user: { firstName: string; lastName: string };
    objectType?: string;
    comment?: string | null;
  };
  previous?: {
    detail: string;
    createTimestamp: string;
    user: { firstName: string; lastName: string };
  } | null;
}

interface IAuditAndHistoryRevisionSingleRecordDisplayProps {}

const AuditAndHistoryRevisionSingleRecordDisplay: React.FC<
  IAuditAndHistoryRevisionSingleRecordDisplayProps
> = (props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const auditId = location.pathname.split("/")[3];

  const { enqueueSnackbar } = useSnackbar();

  // Zustand store
  const selectedRevision = useAppStore(
    (state) => state.auditAndHistoryRevisions?.selectedRevisionContent
  ) as IRevisionContent | null;

  const selectedRevisionLoadPending = useAppStore(
    (state) => state.auditAndHistoryRevisions?.selectedRevisionContentLoadPending || false
  );

  const setSelectedRevisionContent = useAppStore((state) => state.setSelectedRevisionContent);
  const setSelectedRevisionContentLoadedSuccessfully = useAppStore((state) => state.setSelectedRevisionContentLoadedSuccessfully);
  const setSelectedRevisionContentLoadedError = useAppStore((state) => state.setSelectedRevisionContentLoadedError);
  const setSelectedRevisionContentLoadPending = useAppStore((state) => state.setSelectedRevisionContentLoadPending);

  React.useEffect(() => {
    setSelectedRevisionContentLoadPending();
    getAuditSummary(Number(auditId))
      .then((response: { data?: unknown }) => {
        if (response && response.data) {
          const { data } = response;
          setSelectedRevisionContent(data);
          setSelectedRevisionContentLoadedSuccessfully();
        }
      })
      .catch((error: unknown) => {
        setSelectedRevisionContentLoadedError(error);
        console.log(error);
      });
  }, []);

  const [commentRequestPending, setCommentRequestPending] = React.useState(
    false
  );
  const [newCommentValue, setNewCommentValue] = React.useState("");

  const handleAddCommentSubmit = (e: React.FormEvent<EventTarget>) => {
    e.preventDefault();
    if (newCommentValue !== "") {
      setCommentRequestPending(true);
      setComment(Number(auditId), newCommentValue)
        .then((response: { data?: unknown }) => {
          if (response && response.data) {
            const { data } = response;
            setSelectedRevisionContent({
              ...(selectedRevision || {}),
              current: data as IRevisionContent['current'],
            });
            setNewCommentValue("");
            setCommentRequestPending(false);
            enqueueSnackbar("comment added!", { variant: "success" });
          }
        })
        .catch((error: unknown) => {
          enqueueSnackbar("error: " + error, { variant: "error" });
        });
    } else {
      setCommentRequestPending(false);
      enqueueSnackbar("Please enter a valid comment in the text area below", {
        variant: "error",
      });
    }
  };

  return (
    <Grid container className="pb-4">
      {selectedRevisionLoadPending ? (
        <Grid container className="pt-3" direction="row" justifyContent="center">
          <GenericSpinner includeOverlay={false} padding={0} />
        </Grid>
      ) : (
        <Grid container className={"pt-3"}>
          <Grid size={{ md: 12 }}>
            <Link className={"back-btn pt-2"} onClick={() => navigate(-1)} style={{ cursor: 'pointer' }}>
              {"< "}Back to Previous Page
            </Link>
          </Grid>
          <Grid size={{ xs: 12, md: 12 }} className="pt-3">
            <h4 style={{ color: "#cccccc" }}>Revision Change</h4>
          </Grid>
          <Grid size={{ md: 12 }}>
            {selectedRevision?.current && (
              <h5 style={{ color: "#cccccc" }}>
                {`${
                  selectedRevision.current.user.firstName
                } ${
                  selectedRevision.current.user.lastName
                }`}{" "}
                made the following changes to{" "}
                {selectedRevision.current.objectType} on{" "}
                {dayjs(
                  new Date(
                    selectedRevision.current.createTimestamp
                  ).toLocaleDateString()
                ).format("MMMM DD, YYYY")}{" "}
                at{" "}
                {dayjs(
                  new Date(selectedRevision.current.createTimestamp)
                ).format("hh:mm:ss a")}
              </h5>
            )}
          </Grid>
          {selectedRevision?.current && (
            <Grid size={{ xs: 12, md: 12 }}>
              <AuditAndHistoryRevisionSingleRecordContentDisplay
                selectedRevision={selectedRevision}
              />
            </Grid>
          )}

          {selectedRevision?.current && (
            <Grid size={{ xs: 12, md: 12 }} className={"pt-2"}>
              <span>
                {selectedRevision.current.comment !== null
                  ? "Previous Comments:"
                  : "No comments available..."}
              </span>
            </Grid>
          )}
          {selectedRevision?.current &&
            selectedRevision.current.comment != null && (
              <Grid
                size={{ xs: 12, md: 12 }}
                style={{
                  height: "auto",
                  maxHeight: "20%",
                  overflowY: "scroll",
                }}
              >
                <List dense={true}>
                  {selectedRevision.current.comment!
                    .split("\n")
                    .map((comment: string, index: number) => {
                      if (comment !== "") {
                        let commentSplitStr = comment.split("-");
                        return (
                          <ListItem key={index}>
                            <ListItemText
                              primary={`${commentSplitStr[1]}`}
                              secondary={commentSplitStr[0]}
                            />
                          </ListItem>
                        );
                      }
                      return;
                    })}
                </List>
              </Grid>
            )}
          {commentRequestPending ? (
            <Grid container className="pt-3" direction="row" justifyContent="center">
              <GenericSpinner includeOverlay={false} padding={0} />
            </Grid>
          ) : (
            <Grid container className={"pt-3"}>
              <Grid size={{ xs: 12, md: 12 }}>
                <TextField
                  fullWidth
                  id={`selected-revision-${auditId}-comment`}
                  label={`Audit #${auditId} Comment...`}
                  value={newCommentValue}
                  onChange={(e: any) => setNewCommentValue(e.target.value)}
                  multiline
                  rows={12}
                  variant="outlined"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 12 }} className={"pt-2"}>
                <Button
                  type="button"
                  size="large"
                  variant="contained"
                  color="primary"
                  className="btn"
                  onClick={handleAddCommentSubmit}
                >
                  Add Comment
                </Button>
              </Grid>
            </Grid>
          )}
        </Grid>
      )}
    </Grid>
  );
};

export default AuditAndHistoryRevisionSingleRecordDisplay;
